/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { jsx } from '@emotion/react'
import { useMemo } from 'react'

import { EuiFlexGroup, EuiFlexItem } from '@elastic/eui'

import ViewBySelect from '../ViewBySelect'
import ChartTypeToggle from '../ChartTypeToggle'
import CumulativeViewSwitch from '../CumulativeViewSwitch'
import { Action } from '../filtersReducer'
import useFilters from '../useFilters'

import type { FunctionComponent } from 'react'

interface Props {
  isLoading: boolean
}

const ChartFilters: FunctionComponent<Props> = ({ isLoading }) => {
  const { filters, dispatch } = useFilters()

  const renderViewBySelect = useMemo(
    () => (
      <ViewBySelect
        value={filters.viewBy}
        onChange={(viewBy) => dispatch({ type: Action.SET_VIEW_BY, payload: viewBy })}
        isLoading={isLoading}
      />
    ),
    [dispatch, isLoading, filters.viewBy],
  )

  const renderChartTypeToggle = useMemo(
    () => (
      <ChartTypeToggle
        onChange={(chartType) => dispatch({ type: Action.SET_CHART_TYPE, payload: chartType })}
        selectedChartType={filters.chartType}
        isLoading={isLoading}
      />
    ),
    [dispatch, isLoading, filters.chartType],
  )

  const renderCumulativeViewSwitch = useMemo(
    () => (
      <CumulativeViewSwitch
        value={filters.cumulativeView}
        onChange={(cumulativeView) =>
          dispatch({ type: Action.SET_CUMULATIVE_VIEW, payload: cumulativeView })
        }
        isLoading={isLoading}
      />
    ),
    [dispatch, isLoading, filters.cumulativeView],
  )

  return (
    <EuiFlexGroup alignItems='center' responsive={false} wrap={true}>
      <EuiFlexItem grow={2} css={{ minWidth: 100 }}>
        {renderViewBySelect}
      </EuiFlexItem>
      <EuiFlexItem grow={0}>{renderChartTypeToggle}</EuiFlexItem>
      <EuiFlexItem grow={6}>{renderCumulativeViewSwitch}</EuiFlexItem>
    </EuiFlexGroup>
  )
}

export default ChartFilters
